import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { SlidersHorizontal, Search, Save, Check, Lock } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── NOVAA dark tokens ───────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .mc-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .mc-input, .mc-num {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 8px; color: var(--nd-text); font-family: 'DM Mono', monospace;
    font-size: 13px; padding: 6px 8px; outline: none;
  }
  .mc-input:focus, .mc-num:focus { border-color: rgba(96,165,250,0.5); }
  .mc-num { width: 64px; text-align: center; }
  .mc-search {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; padding: 8px 12px 8px 34px; outline: none; width: 260px;
  }
  .mc-table { width: 100%; border-collapse: collapse; }
  .mc-table th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
    color: var(--nd-tx35); text-transform: uppercase; padding: 10px 12px;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap;
  }
  .mc-table td { font-size: 13px; color: var(--nd-text); padding: 8px 12px; border-bottom: 1px solid var(--nd-w04); }
  .mc-table tr:hover td { background: var(--nd-w02); }
  .mc-save {
    background: rgba(96,165,250,0.15); border: 1px solid rgba(96,165,250,0.35);
    border-radius: 8px; color: #60A5FA; font-weight: 700; font-size: 12px;
    padding: 6px 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  }
  .mc-save:hover { background: rgba(96,165,250,0.25); }
  .mc-save:disabled { opacity: 0.4; cursor: default; }
  .mc-saved { color: #22D3A5; display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; }
`;

const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0; };

const MandateConfig = () => {
  const [rows, setRows] = useState([]);          // server rows
  const [edits, setEdits] = useState({});        // client_id -> {sup,fo,eng,tech,hpd}
  const [savedIds, setSavedIds] = useState({});  // client_id -> true (transient)
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/ci-performance/mandates`);
      const data = res.data?.data || [];
      setRows(data);
      const e = {};
      for (const r of data) {
        e[r.client_id] = {
          sup: Number(r.man_hours_supervisor) || 0,
          fo: Number(r.man_hours_fo) || 0,
          eng: Number(r.man_hours_engineer) || 0,
          tech: Number(r.man_hours_technician) || 0,
          hpd: Number(r.hours_per_member_per_day) || 8,
        };
      }
      setEdits(e);
    } catch (err) {
      console.error('Mandates load error:', err);
      setError('Failed to load mandates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setField = (clientId, field, value) => {
    setEdits((prev) => ({ ...prev, [clientId]: { ...prev[clientId], [field]: value } }));
    setSavedIds((prev) => ({ ...prev, [clientId]: false }));
  };

  const saveRow = async (clientId) => {
    const e = edits[clientId];
    if (!e) return;
    try {
      setSavingId(clientId);
      await axios.put(`${API}/ci-performance/mandates/${clientId}`, {
        man_hours_supervisor: n(e.sup),
        man_hours_fo: n(e.fo),
        man_hours_engineer: n(e.eng),
        man_hours_technician: n(e.tech),
        hours_per_member_per_day: n(e.hpd) || 8,
      });
      setSavedIds((prev) => ({ ...prev, [clientId]: true }));
      setTimeout(() => setSavedIds((prev) => ({ ...prev, [clientId]: false })), 2500);
    } catch (err) {
      console.error('Mandate save error:', err);
      alert('Failed to save mandate for this client.');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.client_name || '').toLowerCase().includes(s));
  }, [rows, search]);

  return (
    <div className="mc-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SlidersHorizontal size={22} color="#F59E0B" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lock size={10} /> HIDDEN · C&I CONFIG
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Client Mandate Man-Hours
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Man-hours required to complete one cycle, per client. Total = the four columns summed.</div>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <Search size={15} color="var(--nd-tx35)" style={{ position: 'absolute', left: 11, top: 9 }} />
            <input className="mc-search" placeholder="Search client…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mc-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'center' }}>Supervisor</th>
                  <th style={{ textAlign: 'center' }}>FO</th>
                  <th style={{ textAlign: 'center' }}>Engineer</th>
                  <th style={{ textAlign: 'center' }}>Technician</th>
                  <th style={{ textAlign: 'center' }}>Hrs/Day</th>
                  <th style={{ textAlign: 'center' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const e = edits[r.client_id] || { sup: 0, fo: 0, eng: 0, tech: 0, hpd: 8 };
                  const total = n(e.sup) + n(e.fo) + n(e.eng) + n(e.tech);
                  return (
                    <tr key={r.client_id}>
                      <td style={{ fontWeight: 700, minWidth: 180 }}>
                        {r.client_name}
                        {!r.has_mandate && <span style={{ marginLeft: 8, fontSize: 9, color: '#F59E0B', fontWeight: 700 }}>UNSET</span>}
                      </td>
                      <td style={{ color: T.muted, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{r.contract_type || '—'}</td>
                      {['sup', 'fo', 'eng', 'tech', 'hpd'].map((f) => (
                        <td key={f} style={{ textAlign: 'center' }}>
                          <input
                            className="mc-num"
                            type="number"
                            min="0"
                            value={e[f]}
                            onChange={(ev) => setField(r.client_id, f, ev.target.value)}
                          />
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", fontWeight: 800, color: '#60A5FA' }}>{total}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {savedIds[r.client_id] ? (
                          <span className="mc-saved"><Check size={14} /> Saved</span>
                        ) : (
                          <button className="mc-save" disabled={savingId === r.client_id} onClick={() => saveRow(r.client_id)}>
                            <Save size={13} /> {savingId === r.client_id ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MandateConfig;
